import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS } from '../i18n';
import { COMPARE_FOOTER_LINKS } from '../pages/compare/compareFooterLinks';
import FooterLocationCard from './FooterLocationCard';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const footerLinks = {
    product: [
      { labelKey: 'lite', href: '/lite', internal: true },
      { labelKey: 'howItWorks', href: '/how-it-works', internal: true },
      { labelKey: 'brandedTracking', href: '/features/branded-tracking' },
      { labelKey: 'routePlanner', href: '/features/route-planner' },
      { labelKey: 'aiAgents', href: '/features/ai-agents' },
      { labelKey: 'autoAllocation', href: '/features/auto-allocation' },
      { labelKey: 'proofOfDelivery', href: '/features/proof-of-delivery' },
      { labelKey: 'driverApp', href: '/features/driver-app' },
      { labelKey: 'cashManagement', href: '/features/cash-management' },
      { labelKey: 'reportsAnalytics', href: '/features/reports-analytics' },
    ],
    solutions: [
      { labelKey: 'restaurants', href: '/industries/restaurants' },
      { labelKey: 'pharmacies', href: '/industries/pharmacies' },
      { labelKey: 'grocery', href: '/industries/grocery' },
      { labelKey: 'retail', href: '/industries/retail' },
      { labelKey: 'ecommerce', href: '/industries/ecommerce' },
      { labelKey: 'couriers', href: '/industries/couriers' },
      { labelKey: 'allIndustries', href: '/industries' },
    ],
    resources: [
      { labelKey: 'caseStudies', href: '/resources/case-studies', internal: true },
      { labelKey: 'blog', href: '/blog', internal: true },
      { labelKey: 'roiCalculator', href: '/roi-calculator', internal: true },
    ],
    company: [
      { labelKey: 'aboutUs', href: '/about', internal: true },
      { labelKey: 'pricing', href: '/pricing', internal: true },
      { labelKey: 'partners', href: '/partners', internal: true },
      { labelKey: 'integrations', href: '/integrations', internal: true },
      { labelKey: 'contact', href: '/contact', internal: true },
    ],
  };

  const socialLinks = [
    { icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/company/geofleetai/', label: 'LinkedIn' },
    { icon: 'ri-twitter-x-fill', href: 'https://x.com/geofleetai', label: 'X' },
    { icon: 'ri-youtube-fill', href: 'https://www.youtube.com/@getgeofleet', label: 'YouTube' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-7 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/">
              <img
                src="https://cdn.geofleet.ai/images/logo.svg"
                alt="Geofleet.ai"
                className="h-8 mb-6 brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              {t('footer.tagline')}
            </p>
            <p className="text-xs text-gray-500 leading-relaxed mb-6 max-w-sm">
              {t('footer.aiCommandHint')}
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-[#1769ff] transition-colors group cursor-pointer"
                >
                  <i className={`${social.icon} text-gray-400 group-hover:text-white transition-colors`}></i>
                </a>
              ))}
            </div>
            {/* Security & compliance badges — small */}
            <div className="flex flex-wrap items-center gap-2 mt-6 pt-6 border-t border-gray-800">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-800/80 text-gray-400 text-[10px] font-medium" title="ISO 27001">
                <i className="ri-shield-check-line text-xs"></i>
                ISO 27001
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-800/80 text-gray-400 text-[10px] font-medium" title="SOC 2">
                <i className="ri-shield-star-line text-xs"></i>
                SOC 2
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-800/80 text-gray-400 text-[10px] font-medium" title="VAPT">
                <i className="ri-bug-line text-xs"></i>
                VAPT
              </span>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase mb-4 tracking-wide">{t('footer.product')}</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
                    {t(`footer.links.${link.labelKey}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Links */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase mb-4 tracking-wide">{t('footer.builtFor')}</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
                    {t(`footer.links.${link.labelKey}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase mb-4 tracking-wide">{t('footer.resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  {link.internal ? (
                    <Link to={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
                      {t(`footer.links.${link.labelKey}`)}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {t(`footer.links.${link.labelKey}`)}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase mb-4 tracking-wide">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  {link.internal ? (
                    <Link to={link.href} className="text-sm text-gray-400 hover:text-white transition-colors">
                      {t(`footer.links.${link.labelKey}`)}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {t(`footer.links.${link.labelKey}`)}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Compare / alternatives */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase mb-4 tracking-wide">{t('footer.compare')}</h4>
            <ul className="space-y-3">
              {COMPARE_FOOTER_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-gray-400 hover:text-white transition-colors">
                    {t(`footer.links.${link.labelKey}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Headquarters */}
        <div className="mb-12">
          <h4 className="text-white text-sm font-bold uppercase mb-4 tracking-wide">{t('footer.location.title')}</h4>
          <FooterLocationCard />
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0">
            <p className="text-sm text-gray-500">© {new Date().getFullYear()} Geofleet.ai. {t('footer.rights')}</p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 uppercase tracking-wider">{t('footer.language')}</span>
                {SUPPORTED_LANGS.map((lang, idx) => (
                  <span key={lang.code} className="flex items-center gap-2">
                    {idx > 0 && <span className="text-gray-600">·</span>}
                    <button
                      type="button"
                      onClick={() => i18n.changeLanguage(lang.code)}
                      className={`text-sm font-medium transition-colors ${
                        i18n.language === lang.code ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      {lang.code.toUpperCase()}
                    </button>
                  </span>
                ))}
              </div>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">{t('footer.terms')}</Link>
              <Link to="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">{t('footer.privacy')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
